import { useTranslation } from "react-i18next";

import { useSetFilter } from "@hooks";
import type { IAvailableDoctorListEndpoint } from "@/api/doctors";
import { useAvailableDoctorList } from "@/api/doctors";
import type { IAvailableDoctorCardProps } from "@/components/molecules/availableDoctorCard";
import { AvailableDoctorGridCard } from "@/components/molecules/availableDoctorCard";
import { PageTable, useTableParams } from "@/components/organisms/pageTable";
import { Grid } from "@/components/atoms/Grid";
import type { IDoctorListProps } from "@/templates/availableDoctorList/types";

function getAvailabilityValue(data: IAvailableDoctorListEndpoint) {
  return data.visit_types.reduce(
    (i, visitType) => i + Number(visitType.available),
    0,
  );
}

function getDoctorCardProps(
  item: IAvailableDoctorListEndpoint,
  props: IDoctorListProps,
  textMode: IAvailableDoctorCardProps["visitTimeTextMode"],
): IAvailableDoctorCardProps {
  const { onSelect, onView, showVisitType } = props;

  const facility = { key: item.facility.id, value: item.facility.name };

  return {
    address: item.facility.address,
    avatar: item.avatar?.url,
    firstName: item.first_name,
    lastName: item.last_name,
    specialty: item.speciality,
    subSpecialty: item.sub_speciality,
    hf: facility,
    visitType: item.visit_types,
    score: item.score.score ? Number(item.score.score?.toFixed(2)) : 0,
    scoreCount: item.score.score_count,
    onGetVisitClick: (visitType, cost) => onSelect(visitType, cost, item),
    onView: onView === undefined ? undefined : () => onView?.(item),
    showVisitType,
    visitTimeTextMode: textMode,
  };
}

export function AvailableDoctorList(props: IDoctorListProps) {
  const { layout = "dynamic", ...propsRest } = props;
  const { t } = useTranslation("form");
  const {
    paginationValues,
    searchValue,
    tableProps,
    filterValues,
    setFilters,
  } = useTableParams({ index: 1 });
  const { currentPage, resultsPerPage } = paginationValues;

  const { data, isFetching, isError, isLoading } = useAvailableDoctorList({
    variables: {
      page: currentPage,
      per_page: resultsPerPage,
      keyword: searchValue,
      ...filterValues,
    },
  });

  useSetFilter({ filters: data?.data?.filters, setFilters });

  return (
    <PageTable
      isFetching={isFetching}
      isError={isError}
      placeholder={"userManagement.doctor"}
      title={t("listBoxTitle.doctor")}
      type={"row"}
      total={data?.data.page.total}
      isLoading={isLoading}
      {...tableProps}
    >
      <Grid noMarginBottom noMarginTop>
        {data?.data.page.list
          .sort(
            (first, second) =>
              getAvailabilityValue(second) - getAvailabilityValue(first),
          )
          .map((item) => {
            return (
              <AvailableDoctorGridCard
                key={item?.id}
                {...getDoctorCardProps(
                  item,
                  { ...propsRest },
                  layout === "dynamic" ? "full" : "chip",
                )}
              />
            );
          })}
      </Grid>
    </PageTable>
  );
}
